import { useEffect } from "react";
import { motion } from "framer-motion";
import { FAQS } from "./FAQ";

const PRODUCT_KEYWORDS = [
  "Stud nut manufacturer in India",
  "Stud bolt manufacturer in Vadodara",
  "ASTM A193 B7 stud bolt supplier",
  "ASTM A194 2H heavy hex nut manufacturer",
  "ASTM A193 B8 / B8M stainless stud bolts",
  "ASTM A320 L7 low temperature stud bolts",
  "High tensile fasteners supplier India",
  "Stainless steel 304 / 316 / 321 fasteners",
  "Hex head bolts class 8.8 / 10.9 / 12.9",
  "Threaded rod and tie rod manufacturer",
  "Countersunk bolts supplier",
  "Washers plain, spring and taper",
  "Hot dip galvanised stud bolts",
  "PTFE Xylan coated stud bolts and nuts",
  "Duplex 2205 and special alloy fasteners",
  "Customised engineering solutions to drawing",
  "Flange bolting kits for ASME B16.5 flanges",
  "Custom machined components manufacturer",
];

const APPLICATION_KEYWORDS = [
  "Refinery flange bolting supplier",
  "Petrochemical stud bolt vendor",
  "Power plant bolting fasteners",
  "Fertiliser plant fastener supplier",
  "Nuclear and atomic research fasteners",
  "Pipeline and cross-country bolting",
  "Heat exchanger and valve stud bolts",
  "Boiler and turbine fastener supplier",
  "Railway and rolling stock fasteners",
  "Defence approved fastener manufacturer",
  "Shutdown and turnaround bolting supply",
  "EPC project bolting scope vendor",
];

const CITIES = [
  "Vadodara", "Ahmedabad", "Surat", "Rajkot", "Bharuch", "Ankleshwar", "Jamnagar",
  "Dahej", "Hazira", "Vapi", "Mumbai", "Pune", "Nashik", "Aurangabad", "Indore",
  "Bhopal", "Jaipur", "Delhi NCR", "Chennai", "Coimbatore", "Bengaluru", "Hyderabad",
  "Visakhapatnam", "Kolkata", "Jamshedpur", "Ludhiana", "Kochi", "Guwahati",
];

const PRODUCT_LIST = [
  {
    name: "Stud Bolts & Threaded Rods",
    description:
      "Fully threaded stud bolts and threaded rods to ASTM A193 B7, B16, B8, B8M and ASTM A320 L7, M12 to M100 and ¼\" to 4\", any length, UNC / UNF / metric threads.",
  },
  {
    name: "Stud Nuts & Heavy Hex Nuts",
    description:
      "Heavy hex nuts and stud nuts to ASTM A194 Gr. 2H, 2HM, 4, 7, 8 and 8M matched to stud grade, supplied with mill test certificates.",
  },
  {
    name: "Hex Head Bolts",
    description:
      "High tensile hex head bolts to IS 1367 / ISO 898-1 class 8.8, 10.9 and 12.9 and ASTM A325 / A490, M6 to M64.",
  },
  {
    name: "Countersunk Bolts",
    description:
      "Flush-finish countersunk flat head bolts to DIN 7991 / IS 1364 in mild steel, high tensile and stainless steel.",
  },
  {
    name: "Customised Engineering Solutions",
    description:
      "Non-standard fasteners and machined components manufactured to client drawing, sample or sketch — special heads, undercut and stepped studs, tie rods and bespoke turned parts.",
  },
];

const ORG_ID = "https://rahilengineering.com/#organisation";

const LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["LocalBusiness", "Manufacturer", "Organization"],
      "@id": ORG_ID,
      name: "Rahil Engineering Works",
      alternateName: ["REW Fasteners", "Rahil Engineering Works Vadodara"],
      description:
        "ISO 9001:2015 certified manufacturer and supplier of stud nuts, stud bolts, heavy hex nuts, hex bolts, countersunk bolts, threaded rods, washers and customised engineering solutions in high tensile and stainless steel. Established 2007, Makarpura GIDC, Vadodara, Gujarat, India.",
      slogan: "Quality is never by chance, it is by choice",
      foundingDate: "2007",
      url: "https://rahilengineering.com/",
      logo: "https://rahilengineering.com/rew-logo.png",
      image: "https://rahilengineering.com/rew-logo.png",
      telephone: "+91-8000151777",
      email: "sales@rahilengineering.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "303/22 Makarpura GIDC",
        addressLocality: "Vadodara",
        addressRegion: "Gujarat",
        postalCode: "390010",
        addressCountry: "IN",
      },
      geo: { "@type": "GeoCoordinates", latitude: 22.2468, longitude: 73.1932 },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          opens: "09:30",
          closes: "19:00",
        },
      ],
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "+91-8000151777",
          contactType: "sales",
          areaServed: "IN",
          availableLanguage: ["en", "hi", "gu"],
        },
      ],
      areaServed: [
        { "@type": "Country", name: "India" },
        ...CITIES.slice(0, 12).map((c) => ({ "@type": "City", name: c })),
      ],
      priceRange: "$$",
      currenciesAccepted: "INR",
      paymentAccepted: "Bank transfer, RTGS, NEFT, LC",
      hasCredential: [
        "ISO 9001:2015",
        "GST Registered",
        "MSME / Udyam Registered",
        "IEC — Import Export Code",
        "GeM Registered Seller",
      ],
      knowsAbout: [...PRODUCT_KEYWORDS, ...APPLICATION_KEYWORDS],
      makesOffer: PRODUCT_LIST.map((p) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Product",
          name: p.name,
          description: p.description,
          brand: { "@type": "Brand", name: "Rahil Engineering Works" },
        },
        availability: "https://schema.org/InStock",
        priceCurrency: "INR",
      })),
    },
    {
      "@type": "WebSite",
      "@id": "https://rahilengineering.com/#website",
      url: "https://rahilengineering.com/",
      name: "Rahil Engineering Works — Stud Nut & Fastener Manufacturer",
      inLanguage: "en-IN",
      publisher: { "@id": ORG_ID },
    },
    {
      "@type": "ItemList",
      name: "Fastener product range",
      itemListElement: PRODUCT_LIST.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.name,
          description: p.description,
          material: "Carbon steel, alloy steel, stainless steel 304/316/321, duplex",
          manufacturer: { "@id": ORG_ID },
          offers: {
            "@type": "Offer",
            availability: "https://schema.org/InStock",
            priceCurrency: "INR",
            priceSpecification: {
              "@type": "PriceSpecification",
              valueAddedTaxIncluded: false,
              description: "Quoted per enquiry based on grade, size and quantity",
            },
          },
        },
      })),
    },
    {
      "@type": "Service",
      name: "Custom fastener manufacturing to drawing",
      serviceType: "Fastener manufacturing and machining",
      provider: { "@id": ORG_ID },
      areaServed: { "@type": "Country", name: "India" },
      description:
        "Manufacturing of non-standard fasteners and machined components to client drawing, sample or sketch, from prototype to bulk, with mill test certificates and third-party inspection support.",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

const KeywordColumn = ({ title, items, testid }) => (
  <div>
    <div className="font-sans font-semibold text-[11px] tracking-[0.16em] uppercase text-brand mb-4">
      {title}
    </div>
    <ul className="space-y-2" data-testid={testid}>
      {items.map((k) => (
        <li key={k} className="text-[13px] text-slate-600 leading-snug flex gap-2">
          <span className="text-brand">·</span>
          {k}
        </li>
      ))}
    </ul>
  </div>
);

export default function SeoContent() {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "rew-structured-data";
    el.text = JSON.stringify(LD);
    document.head.appendChild(el);
    return () => {
      document.getElementById("rew-structured-data")?.remove();
    };
  }, []);

  return (
    <section className="bg-white border-t border-steel-light py-16 lg:py-20" data-testid="seo-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 font-sans font-semibold text-[11px] tracking-[0.18em] uppercase text-steel mb-4">
              <span className="w-10 h-[2px] bg-brand" />
              Supply Coverage
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.05] text-3xl sm:text-4xl text-ink">
              Stud nut & fastener
              <br />
              supplier from Vadodara
              <br />
              to all of India
            </h2>
            <p className="mt-5 text-sm text-slate-600 leading-relaxed">
              Rahil Engineering Works manufactures stud nuts, stud bolts, heavy
              hex nuts, hex bolts, countersunk bolts, threaded rods, washers and
              customised engineering solutions at Makarpura GIDC, Vadodara,
              Gujarat. Established in 2007 and ISO 9001:2015 certified, we
              supply refineries, petrochemical complexes, power stations,
              fertiliser plants, EPC contractors and OEM factories across India
              and on export orders — every batch released with mill test
              certificates and full batch traceability.
            </p>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
              Sizes from M12 to M100 and ¼" to 4" in any length, threads in UNC,
              UNF and metric, finishes in self colour, black, zinc plated, hot
              dip galvanised and PTFE Xylan. Need a grade that is not listed?
              Send the drawing — we manufacture to specification.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-navy text-white text-[11px] tracking-[0.14em] uppercase font-bold px-5 py-3 rounded-lg hover:bg-brand transition-colors duration-300"
                data-testid="seo-cta-rfq"
              >
                Request a quotation
              </a>
              <a
                href="tel:+918000151777"
                className="inline-flex items-center gap-2 border border-steel-light text-navy text-[11px] tracking-[0.14em] uppercase font-bold px-5 py-3 rounded-lg hover:border-brand hover:text-brand transition-colors duration-300"
                data-testid="seo-cta-call"
              >
                Call 8000151777
              </a>
            </div>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-8">
            <KeywordColumn
              title="Products we manufacture"
              items={PRODUCT_KEYWORDS}
              testid="seo-keywords"
            />
            <div className="space-y-8">
              <KeywordColumn
                title="Applications we supply"
                items={APPLICATION_KEYWORDS}
                testid="seo-applications"
              />
              <div>
                <div className="font-sans font-semibold text-[11px] tracking-[0.16em] uppercase text-brand mb-4">
                  Cities & regions we dispatch to
                </div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="flex flex-wrap gap-2"
                  data-testid="seo-cities"
                >
                  {CITIES.map((c) => (
                    <span
                      key={c}
                      className="text-[11px] text-navy bg-mist border border-steel-light rounded-full px-3 py-1.5 hover:border-brand hover:text-brand transition-colors duration-300"
                    >
                      {c}
                    </span>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
